"use client";
'use strict';

var React = require('react');
var useBoolean = require('./useBoolean.js');

var HTTPMethod;
(function (HTTPMethod) {
    HTTPMethod["DELETE"] = "DELETE";
    HTTPMethod["GET"] = "GET";
    HTTPMethod["HEAD"] = "HEAD";
    HTTPMethod["PATCH"] = "PATCH";
    HTTPMethod["POST"] = "POST";
    HTTPMethod["PUT"] = "PUT";
})(HTTPMethod || (HTTPMethod = {}));
/**
 * React Hook which aims to retrieve data on an API using the native Fetch API
 * If the last argument of useFetch is not a dependency array [], then it will not fire until you call one of the http methods like get, post, etc.
 *
 * @param url
 * @param options
 * @param deps
 * @example
 * ```javascript
 * const [{ data, error, loading }, cancel] = useFetch<Data>(url, options)
 * ```
 */
function useFetch(url, options, deps) {
    const [loading, setLoading] = useBoolean.useBoolean(false);
    const [data, setData] = React.useState(null);
    const [error, setError] = React.useState(null);
    const controller = React.useRef();
    const cancel = () => {
        controller.current?.abort();
    };
    const makeFetch = (method) => async (body) => {
        let route = url;
        const init = {
            headers: {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                'Content-Type': 'application/json',
            },
            ...options,
            method,
            signal: controller.current?.signal,
        };
        if (body) {
            if (method === HTTPMethod.GET || method === HTTPMethod.HEAD) {
                // eslint-disable-next-line @typescript-eslint/init-declarations
                let stringParams;
                if (body instanceof URLSearchParams) {
                    stringParams = body.toString();
                }
                else {
                    // @ts-ignore
                    stringParams = new URLSearchParams(body).toString();
                }
                route = `${route}?${stringParams}`;
            }
            else if (body instanceof FormData || body instanceof URLSearchParams) {
                init.body = body;
            }
            else {
                init.body = JSON.stringify(body);
            }
        }
        controller.current = new AbortController();
        setLoading.on();
        setError(null);
        try {
            const response = await fetch(route, init);
            if (!response.ok) {
                throw new Error(response.statusText);
            }
            const data = (await response.json());
            setData(data);
        }
        catch (error) {
            setError(error);
        }
        finally {
            controller.current = undefined;
        }
        setLoading.off();
    };
    const get = React.useCallback(makeFetch(HTTPMethod.GET), [url, options]);
    const post = React.useCallback(makeFetch(HTTPMethod.POST), [url, options]);
    const del = React.useCallback(makeFetch(HTTPMethod.DELETE), [url, options]);
    const patch = React.useCallback(makeFetch(HTTPMethod.PATCH), [url, options]);
    const put = React.useCallback(makeFetch(HTTPMethod.PUT), [url, options]);
    const head = React.useCallback(makeFetch(HTTPMethod.HEAD), [url, options]);
    React.useEffect(() => {
        if (!Array.isArray(deps)) {
            return;
        }
        makeFetch(options?.method || HTTPMethod.GET)();
        // Use the cleanup function for avoiding a possibly...
        // ...state update after the component was unmounted
        return () => {
            cancel();
            setLoading.off();
            controller.current = undefined;
        };
    }, deps);
    return [
        {
            loading,
            error,
            data,
            get,
            post,
            del,
            patch,
            put,
            head,
        },
        cancel,
    ];
}

exports.useFetch = useFetch;
//# sourceMappingURL=useFetch.js.map
